import type { ResolutionLabel } from "./types";

export type SourcePreference = "screen" | "window" | "tab";

export type ResolutionPreset = {
  label: ResolutionLabel;
  name: string;
  width: number | null;
  height: number | null;
};

export const RESOLUTION_PRESETS: Record<ResolutionLabel, ResolutionPreset> = {
  original: { label: "original", name: "Original", width: null, height: null },
  "1440p": { label: "1440p", name: "1440p", width: 2560, height: 1440 },
  "1080p": { label: "1080p", name: "1080p", width: 1920, height: 1080 },
  "900p": { label: "900p", name: "900p", width: 1600, height: 900 },
  "720p": { label: "720p", name: "720p", width: 1280, height: 720 },
};

export const RESOLUTION_OPTIONS = Object.values(RESOLUTION_PRESETS);
export const FPS_OPTIONS = [15, 30, 60] as const;
export const BITRATE_OPTIONS_KBPS = [
  4000, 6000, 8000, 10_000, 12_000, 16_000, 20_000,
] as const;

const DISPLAY_SURFACE_PREFERENCE: Record<
  SourcePreference,
  "monitor" | "window" | "browser"
> = {
  screen: "monitor",
  window: "window",
  tab: "browser",
};

export const SOURCE_PREFERENCES: Array<{
  value: SourcePreference;
  title: string;
  description: string;
}> = [
  {
    value: "screen",
    title: "Весь экран",
    description: "Монитор целиком",
  },
  {
    value: "window",
    title: "Окно",
    description: "Отдельное приложение",
  },
  {
    value: "tab",
    title: "Вкладка",
    description: "Вкладка браузера",
  },
];

export type CaptureInfo = {
  width: number | null;
  height: number | null;
  frameRate: number | null;
  displaySurface: string | null;
  sourceLabel: string;
  hasAudio: boolean;
  warnings: string[];
};

export class CaptureError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "CaptureError";
    this.code = code;
  }
}

type ExtendedDisplayMediaOptions = DisplayMediaStreamOptions & {
  selfBrowserSurface?: "include" | "exclude";
  surfaceSwitching?: "include" | "exclude";
  systemAudio?: "include" | "exclude";
  monitorTypeSurfaces?: "include" | "exclude";
  preferCurrentTab?: boolean;
};

function displaySurfaceLabel(value: string | null): string {
  switch (value) {
    case "monitor":
      return "Весь экран";
    case "window":
      return "Окно приложения";
    case "browser":
      return "Вкладка браузера";
    default:
      return "Источник не определён браузером";
  }
}

export function defaultBitrateKbps(
  label: ResolutionLabel,
  fps: number,
): number {
  if (label === "original") {
    return fps >= 60 ? 12_000 : 8_000;
  }
  const base: Record<Exclude<ResolutionLabel, "original">, number> = {
    "1440p": 12_000,
    "1080p": 9000,
    "900p": 6500,
    "720p": 4500,
  };
  const target = base[label];
  const factor = fps >= 60 ? 1 : fps >= 30 ? 0.8 : 0.5;
  return Math.round((target * factor) / 500) * 500;
}

export function buildDisplayMediaConstraints(input: {
  resolution: ResolutionLabel;
  fps: number;
  source: SourcePreference;
  audio: boolean;
}): ExtendedDisplayMediaOptions {
  const preset = RESOLUTION_PRESETS[input.resolution];
  const videoConstraints: MediaTrackConstraints & {
    displaySurface?: "monitor" | "window" | "browser";
  } = {
    frameRate: { ideal: input.fps, max: input.fps },
    width: preset.width ? { max: preset.width } : undefined,
    height: preset.height ? { max: preset.height } : undefined,
    displaySurface: DISPLAY_SURFACE_PREFERENCE[input.source],
  };

  return {
    video: videoConstraints,
    audio: input.audio,
    selfBrowserSurface: "exclude",
    surfaceSwitching: "include",
    systemAudio: input.audio ? "include" : "exclude",
    monitorTypeSurfaces: "include",
    preferCurrentTab: false,
  };
}

export function readCaptureInfo(
  stream: MediaStream,
  warnings: string[] = [],
): CaptureInfo {
  const track = stream.getVideoTracks()[0];
  const settings = track?.getSettings() ?? {};
  const actualWidth = settings.width ?? null;
  const actualHeight = settings.height ?? null;
  const actualFrameRate =
    typeof settings.frameRate === "number" ? settings.frameRate : null;
  const displaySurface =
    typeof settings.displaySurface === "string"
      ? settings.displaySurface
      : null;

  return {
    width: actualWidth,
    height: actualHeight,
    frameRate: actualFrameRate,
    displaySurface,
    sourceLabel: displaySurfaceLabel(displaySurface),
    hasAudio: stream.getAudioTracks().length > 0,
    warnings,
  };
}

function captureErrorMessage(error: unknown): CaptureError {
  if (error instanceof DOMException) {
    switch (error.name) {
      case "NotAllowedError":
        return new CaptureError(
          "PERMISSION_DENIED",
          "Доступ к захвату экрана отменён или запрещён. Разрешите демонстрацию в браузере.",
        );
      case "NotFoundError":
        return new CaptureError(
          "SOURCE_NOT_FOUND",
          "Браузер не нашёл доступный источник для захвата.",
        );
      case "NotReadableError":
        return new CaptureError(
          "SOURCE_BUSY",
          "Источник занят или недоступен. Закройте защищённые окна и попробуйте снова.",
        );
      case "OverconstrainedError":
        return new CaptureError(
          "CONSTRAINTS_UNSUPPORTED",
          "Браузер не смог применить выбранное разрешение или FPS. Выберите другой источник или настройки.",
        );
      case "AbortError":
        return new CaptureError("ABORTED", "Захват экрана был прерван.");
      default:
        return new CaptureError(
          "CAPTURE_FAILED",
          `Не удалось начать захват: ${error.message}`,
        );
    }
  }
  if (error instanceof Error) {
    return new CaptureError("CAPTURE_FAILED", error.message);
  }
  return new CaptureError("CAPTURE_FAILED", "Не удалось начать захват экрана.");
}

async function applyTrackConstraints(
  track: MediaStreamTrack,
  resolution: ResolutionLabel,
  fps: number,
): Promise<string[]> {
  const preset = RESOLUTION_PRESETS[resolution];
  const constraints: MediaTrackConstraints = {
    frameRate: { ideal: fps, max: fps },
  };
  if (preset.width && preset.height) {
    constraints.width = { max: preset.width };
    constraints.height = { max: preset.height };
  }

  const warnings: string[] = [];
  const before = track.getSettings();
  const beforeWidth = before.width ?? null;
  const beforeHeight = before.height ?? null;

  // Never ask the browser to scale a source up. Some implementations will
  // happily turn a 720p source into "1080p" when given a larger constraint;
  // that would misrepresent the actual image.
  if (
    preset.width &&
    preset.height &&
    beforeWidth &&
    beforeHeight &&
    (beforeWidth < preset.width || beforeHeight < preset.height)
  ) {
    delete constraints.width;
    delete constraints.height;
    warnings.push(
      `Выбранное разрешение ${preset.width} × ${preset.height} выше исходного ${beforeWidth} × ${beforeHeight}. Оставлено исходное изображение; апскейл не выполняется.`,
    );
  }

  try {
    await track.applyConstraints(constraints);
  } catch (error) {
    if (
      error instanceof DOMException &&
      error.name === "OverconstrainedError"
    ) {
      warnings.push(
        "Браузер не поддержал выбранные ограничения. Используется доступное качество источника.",
      );
    } else {
      warnings.push("Не удалось применить настройки к видеотреку.");
    }
  }

  const settings = track.getSettings();
  const actualWidth = settings.width ?? 0;
  const actualHeight = settings.height ?? 0;
  if (
    preset.width &&
    preset.height &&
    actualWidth > 0 &&
    actualHeight > 0 &&
    (actualWidth > preset.width || actualHeight > preset.height)
  ) {
    warnings.push(
      `Браузер оставил разрешение ${actualWidth} × ${actualHeight}. Оно выше выбранного ${preset.width} × ${preset.height}; изображение не увеличено, но лимит не применён.`,
    );
  }

  return warnings;
}

export async function captureDisplay(input: {
  resolution: ResolutionLabel;
  fps: number;
  source: SourcePreference;
  audio: boolean;
}): Promise<{ stream: MediaStream; info: CaptureInfo }> {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new CaptureError(
      "UNSUPPORTED",
      "Ваш браузер не поддерживает захват экрана. Используйте актуальный Chrome, Edge или Firefox.",
    );
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia(
      buildDisplayMediaConstraints(input) as DisplayMediaStreamOptions,
    );
  } catch (error) {
    throw captureErrorMessage(error);
  }

  const track = stream.getVideoTracks()[0];
  if (!track) {
    stream.getTracks().forEach((item) => item.stop());
    throw new CaptureError(
      "NO_VIDEO_TRACK",
      "Браузер не предоставил видеотрек экрана.",
    );
  }

  track.contentHint = "detail";
  const warnings = await applyTrackConstraints(
    track,
    input.resolution,
    input.fps,
  );
  if (input.audio && stream.getAudioTracks().length === 0) {
    warnings.push(
      "Системный звук недоступен для выбранного источника. Видеотрансляция продолжится без звука.",
    );
  }

  return { stream, info: readCaptureInfo(stream, warnings) };
}

export async function applyCaptureSettings(
  stream: MediaStream,
  resolution: ResolutionLabel,
  fps: number,
): Promise<CaptureInfo> {
  const track = stream.getVideoTracks()[0];
  if (!track) {
    throw new CaptureError(
      "NO_VIDEO_TRACK",
      "Видеотрек уже завершён. Выберите источник заново.",
    );
  }
  const warnings = await applyTrackConstraints(track, resolution, fps);
  return readCaptureInfo(stream, warnings);
}

export function stopMediaStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((track) => {
    try {
      track.stop();
    } catch {
      // Already stopped.
    }
  });
}

/**
 * Measures frames actually delivered to the preview element. This is more
 * honest than showing the requested getSettings().frameRate as "actual".
 */
export async function measureVideoFps(
  video: HTMLVideoElement,
  durationMs = 2000,
): Promise<number | null> {
  if (typeof video.requestVideoFrameCallback !== "function") return null;
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return null;

  return new Promise((resolve) => {
    let frames = 0;
    let firstTimestamp: number | null = null;
    let settled = false;
    let callbackId = 0;

    const finish = (value: number | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      try {
        video.cancelVideoFrameCallback(callbackId);
      } catch {
        // Ignore.
      }
      resolve(value);
    };

    const timeout = window.setTimeout(() => {
      if (frames >= 2) {
        const elapsed =
          performance.now() - (firstTimestamp ?? performance.now());
        finish(elapsed > 0 ? (frames * 1000) / elapsed : null);
      } else {
        finish(null);
      }
    }, durationMs + 500);

    const onFrame = (now: number) => {
      if (settled) return;
      frames += 1;
      firstTimestamp ??= now;
      const elapsed = now - firstTimestamp;
      if (elapsed >= durationMs) {
        finish((frames * 1000) / elapsed);
        return;
      }
      callbackId = video.requestVideoFrameCallback(onFrame);
    };
    callbackId = video.requestVideoFrameCallback(onFrame);
  });
}
